import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Button, Container } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import Loader from '../../Components/Utility/Loader/Loader';
import logo from '../../assets/images/bombiix-logo.svg';
import MicrosoftLogo from '../../assets/bombiix-icons/MicrosoftLogo';
import GoogleLogo from '../../assets/bombiix-icons/GoogleLogo';
import './Login.css';
import { Login as LoginAction } from './store/Auth.action';
import { isSsoProviderEnabled, ssoAuthorizeUrl, storeSetupToken } from '../../services/auth.service';
import { toasterMsg } from '../../Components/Toaster/Toaster';

function Login({ redirecting }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ssoProviders, setSsoProviders] = useState({ microsoft: false, google: false });
  const {
    handleSubmit,
    watch,
    register,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
      staySignedIn: false,
      pageLoader: false,
      showPassword: false,
    },
  });
  const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation(['common', 'sso']);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  /**
   * Post-login return target. Restricted to a path on this same origin: an
   * absolute URL here would let any link of the form /login?callback=<attacker>
   * bounce a freshly-authenticated user off-site.
   */
  const callbackUrl = (() => {
    const requested = queryParams.get('callback');
    if (!requested) {
      return null;
    }
    try {
      const resolved = new URL(requested, window.location.origin);
      return resolved.origin === window.location.origin ? `${resolved.pathname}${resolved.search}${resolved.hash}` : null;
    } catch (err) {
      return null;
    }
  })();
  const ssoError = queryParams.get('sso_error');
  const ssoErrorReason = queryParams.get('reason');
  const signedOut = queryParams.get('signed_out') === '1';

  /**
   * Re-enables the submit button when the browser restores this page from the
   * back-forward cache. Every success path navigates away with a full page load,
   * leaving isSubmitting true; a bfcache restore replays that state, so without
   * this the form comes back with a permanently dead Log In button.
   */
  useEffect(() => {
    const handlePageShow = () => setIsSubmitting(false);
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  /** Shows each provider button only for tenants that have that provider configured. */
  useEffect(() => {
    let active = true;
    Promise.all([isSsoProviderEnabled('microsoft'), isSsoProviderEnabled('google')]).then(([microsoft, google]) => {
      if (active) {
        setSsoProviders({ microsoft, google });
      }
    });
    return () => {
      active = false;
    };
  }, []);

  /**
   * Surfaces SSO redirect outcomes as toasts, matching the app's error convention.
   * Reasons are generic by design; a removed account is the one case the server
   * names explicitly, so the person is told to contact their administrator rather
   * than retrying a sign-in that can never succeed.
   */
  useEffect(() => {
    if (ssoError) {
      const messageKey = ssoErrorReason === 'USER_REMOVED' ? 'sso:LOGIN.USER_REMOVED' : 'sso:LOGIN.SSO_FAILED';
      toasterMsg(t(messageKey), 'danger', 6000);
    } else if (signedOut) {
      toasterMsg(t('sso:LOGIN.SIGNED_OUT'), 'info', 6000);
    }
  }, [ssoError, ssoErrorReason, signedOut, t]);

  /** Full-page navigation: the server replies with a redirect to the identity provider. */
  const redirectToSso = (provider) => {
    window.location.href = ssoAuthorizeUrl(provider);
  };

  /** Toggles password visibility. */
  const togglePasswordVisibility = () => {
    setValue('showPassword', !watch('showPassword'));
  };

  const postAPI = async (loginData) => {
    if (!errors?.isError && !errors?.email && !errors?.password) {
      try {
        // If there are no validation errors, proceed with login
        const params = {
          email: loginData?.email,
          password: loginData?.password,
          staySignedIn: loginData?.staySignedIn,
        };

        if (loginData?.rememberMe) {
          params.rememberMe = true;
        } else {
          localStorage.removeItem('rememberMe');
        }
        const loginRes = await dispatch(LoginAction(params, t));
        if (!loginRes) {
          setIsSubmitting(false);
          return;
        }
        if ((loginRes?.redirectToQuestions || loginRes?.returnTomfaEnabled) && !loginRes?.masterPasswordMatched) {
          /** The password is verified but no session exists yet; this token proves which account setup is for. */
          storeSetupToken(loginRes?.setupToken);
          history.push(`/setup2fa?email=${encodeURIComponent(loginData.email)}`);
          return;
        }

        if (loginRes) {
          const previousStates = {
            libraries: '',
            'account-settings': '',
          };
          if (loginData?.rememberMe) {
            localStorage.setItem('rememberMe', true);
          }
          localStorage.setItem('loggedInUserInfo', JSON.stringify(loginRes));
          localStorage.setItem('previousStates', JSON.stringify(previousStates));

          if (loginRes?.mfaEnabled && !loginRes?.mfaVerified && !loginRes?.masterPasswordMatched) {
            history.push('/verify-code');
          } else if (localStorage.getItem('redirectToPO')) {
            const supplierData = localStorage.getItem('redirectToPO');
            window.location.href = `${window.location.origin}${supplierData}`;
          } else if (localStorage.getItem('redirectionToPO')) {
            const poRedirect = localStorage.getItem('redirectionToPO');
            window.location.href = poRedirect;
          } else if (!callbackUrl) {
            window.location.href = `${window.location.origin}/todo-list/my-todo`;
          } else if (callbackUrl) {
            window.location.href = callbackUrl;
          } else {
            window.location.href = `${window.location.origin}/todo-list/sample-requests`;
          } // Redirect to the home page if login is successful
        }
      } catch (error) {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (val) => {
    setIsSubmitting(true);
    await postAPI(val);
  };

  const redirectToForgot = () => {
    history.push('/forgot-password');
  };
  if (redirecting) {
    return <Loader />;
  }

  return (
    <Container>
      <div className="login-container-bombiix">
        <div className="logo-container">
          <img src={logo} className="img-fluid logo-image" alt="logo" />
        </div>
        <div className="mt-40">
          <Form className="login-form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <Form.Group controlId="formBasicEmail">
              <Form.Label className="">
                {t('SETUP_2FA.EMAIL')} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                {...register('email', {
                  required: t('LOGIN.EMAIL_REQUIRED'),
                  pattern: {
                    value: emailPattern,
                    message: t('LOGIN.VALID_EMAIL'),
                  },
                })}
                key="loginEmail"
                id="email_login_super_Admin"
                className={watch('email') && 'data-filled'}
                autoComplete="new-email"
                placeholder={t('SETUP_2FA.ENTER_EMAIL')}
              />

              {errors?.email?.message && <div className="text-danger err-text">{errors?.email?.message}</div>}
            </Form.Group>

            <Form.Group controlId="formBasicPassword">
              <Form.Label>
                {t('LOGIN.PASSWORD')} <span className="text-danger">*</span>
              </Form.Label>
              <div className="password-input-container">
                <Form.Control
                  type={watch('showPassword') ? 'text' : 'password'}
                  key="loginPassword"
                  id="password_login_super_admin"
                  autoComplete="new-password"
                  className={`password-input ${watch('password') && 'data-filled'}`}
                  {...register('password', {
                    required: t('LOGIN.PASSWORD_REQUIRED'),
                  })}
                  placeholder={t('SETUP_2FA.ENTER_PASSWORD')}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={togglePasswordVisibility}
                  aria-label={watch('showPassword') ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas ${watch('showPassword') ? 'icon-Password-Eye-Open' : 'icon-Password-Eye-Close'}`} />
                </button>
              </div>

              {errors?.password?.message && <div className="text-danger err-text">{errors?.password?.message}</div>}
            </Form.Group>

            <div>
              <div className="d-flex justify-content-between align-items-center">
                <Button disabled={isSubmitting} variant="primary" className="btn-theme" type="submit">
                  {t('LOGIN.LOG_IN')}
                </Button>
                <Form.Group className="ms-auto mb-0 " controlId="formBasic">
                  <Form.Check
                    type="checkbox"
                    className="custom-checkbox-signed"
                    label={t('LOGIN.STAY_SIGN_IN')}
                    checked={watch('staySignedIn')}
                    {...register('staySignedIn')}
                  />
                </Form.Group>
              </div>
              <div className="mt-30 text-center">
                <button type="button" onClick={redirectToForgot} className="forgot-text">
                  {t('LOGIN.FORGOT_YOUR_PASSWORD')}
                </button>
              </div>
                {(ssoProviders.microsoft || ssoProviders.google) && (
                  <>
                    <div className="login-divider">
                      <span>{t('LOGIN.OR')}</span>
                    </div>
                    {ssoProviders.microsoft && (
                      <div>
                        <button type="button" className="ms-sso-btn" onClick={() => redirectToSso('microsoft')}>
                          <MicrosoftLogo size={20} className="ms-sso-btn__logo" />
                          <span className="ms-sso-btn__label">{t('sso:LOGIN.SIGN_IN_WITH_MICROSOFT')}</span>
                        </button>
                      </div>
                    )}
                    {ssoProviders.google && (
                      <div className={ssoProviders.microsoft ? 'mt-2' : ''}>
                        <button type="button" className="ms-sso-btn" onClick={() => redirectToSso('google')}>
                          <GoogleLogo size={20} className="ms-sso-btn__logo" />
                          <span className="ms-sso-btn__label">{t('sso:LOGIN.SIGN_IN_WITH_GOOGLE')}</span>
                        </button>
                      </div>
                    )}
                  </>
                )}
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
}

export default Login;
